import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { trackPageView } from "../lib/tracking";
import { resetScrollMilestones } from "../lib/autoTrack";
import { api } from "../lib/api";

// Session id (single random id per browser tab visit) — used to dedupe the
// server-side `page_view` engagement event AND to anchor referrer attribution.
function getSessionId() {
  try {
    let sid = sessionStorage.getItem("fg_session_id_v1");
    if (!sid) {
      sid = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem("fg_session_id_v1", sid);
    }
    return sid;
  } catch {
    return null;
  }
}

let firstNavRecorded = false;

/**
 * Fires a unified page_view on every React Router change.
 *
 * Three side-effects:
 *   1. Resets scroll milestones (`article_read_50/90`) so each article starts a fresh measurement window.
 *   2. Fires `page_view` to the unified tracking facade (PostHog + GA4 + future pixels) — consent-gated.
 *   3. Posts a lightweight `page_view` engagement event to the FeelGreats backend (cookie or anon client_id),
 *      carrying `referrer`, `path`, and `session_id` — feeds the Acquisition Snapshot widget at /admin → Analytics.
 *      First navigation of the session also includes `document.referrer` so traffic-source attribution works.
 *
 * Must be rendered inside a <BrowserRouter>.
 */
export default function RouteAnalytics() {
  const location = useLocation();
  const lastSent = useRef("");

  useEffect(() => {
    const key = `${location.pathname}${location.search}`;
    // Defer one tick so React has rendered + document.title has updated.
    const t = setTimeout(() => {
      if (lastSent.current === key) return;
      lastSent.current = key;
      resetScrollMilestones();
      trackPageView(location.pathname, location.search);

      // Lightweight server-side breadcrumb for the admin snapshot. NEVER blocks
      // the UI — fire-and-forget. Only fires once per route change.
      try {
        const sid = getSessionId();
        const meta = {
          path: location.pathname,
          session_id: sid,
        };
        if (!firstNavRecorded) {
          firstNavRecorded = true;
          // Capture the inbound referrer on the very first nav of the session
          // so the admin can see Google / Pinterest / direct traffic.
          if (typeof document !== "undefined" && document.referrer) {
            meta.referrer = document.referrer;
          }
        }
        api.post("/engagement", {
          event_type: "page_view",
          meta,
        }).catch(() => { /* silent — analytics must never break the app */ });
      } catch { /* ignore */ }
    }, 50);
    return () => clearTimeout(t);
  }, [location.pathname, location.search]);

  return null;
}
